import { logEvent, setUserProperties } from 'firebase/analytics';
import { getFirebaseInstance } from '../firebase/config';

/**
 * Servicio para Firebase Analytics
 * Complementa Meta Pixel con datos adicionales para optimización interna
 */
class FirebaseAnalyticsService {
    constructor() {
        this.analytics = null;
        this.initialized = false;
        this.sessionId = this.generateSessionId();
    }

    /**
     * Generar ID de sesión único
     */
    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Inicializar Analytics
     */
    async initialize() {
        try {
            const firebaseInstance = await getFirebaseInstance();
            this.analytics = firebaseInstance.analytics;

            if (this.analytics) {
                this.initialized = true;
                console.log('📊 Firebase Analytics Service inicializado');

                // Configurar propiedades de sesión
                this.setSessionProperties();
                return true;
            } else {
                console.log('📊 Analytics no disponible - continuando sin Analytics');
                return false;
            }
        } catch (error) {
            console.error('📊 Error inicializando Analytics Service:', error);
            return false;
        }
    }

    /**
     * Configurar propiedades de sesión
     */
    setSessionProperties() {
        if (!this.analytics) return;

        try {
            setUserProperties(this.analytics, {
                session_id: this.sessionId,
                user_agent: navigator.userAgent,
                screen_resolution: `${screen.width}x${screen.height}`,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                language: navigator.language
            });
        } catch (error) {
            console.error('📊 Error configurando propiedades de sesión:', error);
        }
    }

    /**
     * Trackear evento personalizado
     */
    trackEvent(eventName, parameters = {}) {
        if (!this.analytics) return;

        try {
            const eventParams = {
                session_id: this.sessionId,
                timestamp: Date.now(),
                ...parameters
            };

            logEvent(this.analytics, eventName, eventParams);
            console.log('📊 Analytics evento:', eventName, eventParams);
        } catch (error) {
            console.error('📊 Error tracking evento:', error);
        }
    }

    /**
     * Trackear un paso del registro. Cada paso es un evento propio para poder armar el
     * embudo y marcar conversiones en GA4. Nunca mandar datos personales (email, nombre,
     * negocio, CUIT, teléfono): las reglas de Google Analytics lo prohíben.
     */
    async trackRegistro(eventName, parameters = {}) {
        if (!this.initialized) await this.initialize();
        this.trackEvent(eventName, parameters);
    }

    /**
     * Identificadores de la visita, para que el backend atribuya sus eventos a la misma
     * persona. Espera poco y sin ellos devuelve null: el registro no depende de Analytics.
     */
    async identificadores() {
        if (!this.initialized) await this.initialize();
        const idMedicion = this.analytics?.app?.options?.measurementId;
        if (!idMedicion || !window.gtag) return null;

        const pedir = (campo) => new Promise((resolve) => {
            setTimeout(() => resolve(null), 1000);
            window.gtag('get', idMedicion, campo, (valor) => resolve(valor ? String(valor) : null));
        });
        const [clientId, sessionId] = await Promise.all([pedir('client_id'), pedir('session_id')]);
        return clientId ? { clientId, sessionId } : null;
    }

    /**
     * Trackear abandono de formulario
     */
    trackFormAbandonment(formName, completedFields, totalFields, timeSpent) {
        this.trackEvent('form_abandonment', {
            // Se manda al cerrar la página: el beacon sale aunque se esté yendo.
            transport_type: 'beacon',
            form_name: formName,
            completed_fields: completedFields,
            total_fields: totalFields,
            completion_percentage: Math.round((completedFields / totalFields) * 100),
            time_spent_seconds: Math.round(timeSpent / 1000),
            abandonment_point: `${completedFields}/${totalFields}`
        });
    }

    /**
     * Trackear tiempo en página
     */
    trackPageTiming(pageName, timeSpent, interactions = 0) {
        this.trackEvent('page_timing', {
            page_name: pageName,
            time_spent_seconds: Math.round(timeSpent / 1000),
            interactions_count: interactions,
            engagement_rate: interactions > 0 ? 'high' : 'low'
        });
    }

    /**
     * Trackear errores de formulario
     */
    trackFormError(formName, errorType, fieldName, errorMessage) {
        this.trackEvent('form_error', {
            form_name: formName,
            error_type: errorType,
            field_name: fieldName,
            error_message: errorMessage,
            user_agent: navigator.userAgent
        });
    }

    /**
     * Trackear conversión completa
     */
    trackConversion(conversionType, value = 0, currency = 'ARS') {
        this.trackEvent('conversion', {
            conversion_type: conversionType,
            value: value,
            currency: currency,
            conversion_time: new Date().toISOString()
        });
    }

    /**
     * Trackear interacción con elementos específicos
     */
    trackInteraction(elementType, elementName, action) {
        this.trackEvent('user_interaction', {
            element_type: elementType,
            element_name: elementName,
            action: action,
            page_url: window.location.href
        });
    }

    /**
     * Generar reporte de sesión (para debugging)
     */
    getSessionReport() {
        return {
            sessionId: this.sessionId,
            initialized: this.initialized,
            analyticsAvailable: !!this.analytics,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
        };
    }
}

// Instancia global del servicio
const firebaseAnalyticsService = new FirebaseAnalyticsService();

export default firebaseAnalyticsService; 